import { Link } from "react-router-dom";
import type { Rumor } from "@/mocks/rumors";
import { formatHeat, shortDate } from "@/utils/format";

interface ArticleCardProps {
  rumor: Rumor;
  variant?: "grid" | "list" | "wide";
  index?: number;
  headingLevel?: "h2" | "h3";
}

export default function ArticleCard({
  rumor,
  variant = "grid",
  index = 0,
  headingLevel = "h3",
}: ArticleCardProps) {
  const Heading = headingLevel;
  const delay = `${Math.min(index * 55, 330)}ms`;

  const categoryBadge = (
    <Link
      to={`/rumors?cat=${rumor.categorySlug}`}
      className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
    >
      <i className="ri-price-tag-3-line text-xs" />
      {rumor.categoryName}
    </Link>
  );

  if (variant === "list") {
    return (
      <article
        className="group flex gap-4 rounded-lg border border-background-200/70 bg-background-50 p-3 transition-colors hover:border-primary-200 hover:bg-background-100/60 animate-fade-up"
        style={{ animationDelay: delay }}
      >
        <Link to={`/article/${rumor.id}`} className="block w-28 shrink-0 sm:w-36">
          <div className="h-24 w-full overflow-hidden rounded-md sm:h-24">
            <img
              src={rumor.cover}
              alt={`${rumor.title} - 热点内幕独家爆料`}
              title={`${rumor.title} - 热点内幕`}
              className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </Link>
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <div className="mb-1.5 flex items-center gap-2">
            {categoryBadge}
            <span className="hidden sm:flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-fire-line text-primary-500" />
              {formatHeat(rumor.heat)}
            </span>
          </div>
          <Heading className="text-base font-semibold leading-snug text-foreground-950">
            <Link to={`/article/${rumor.id}`} className="transition-colors hover:text-primary-700">
              {rumor.title}
            </Link>
          </Heading>
          <p className="mt-1 hidden text-sm leading-relaxed text-foreground-600 line-clamp-2 md:block">
            {rumor.excerpt}
          </p>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-star-line" />
              {rumor.insider}
            </span>
            <span className="hidden sm:inline">{shortDate(rumor.publishedAt)}</span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === "wide") {
    return (
      <article
        className="group relative overflow-hidden rounded-lg border border-background-200/70 bg-background-50 animate-fade-up"
        style={{ animationDelay: delay }}
      >
        <Link to={`/article/${rumor.id}`} className="block">
          <div className="h-52 w-full overflow-hidden md:h-64">
            <img
              src={rumor.cover}
              alt={`${rumor.title} - 热点内幕独家爆料`}
              title={`${rumor.title} - 热点内幕`}
              className="h-full w-full object-top object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          <div className="p-5">
            <div className="mb-2.5 flex flex-wrap items-center gap-2">
              {categoryBadge}
              <span className="flex items-center gap-1 text-xs text-foreground-500">
                <i className="ri-fire-line text-primary-500" />
                {formatHeat(rumor.heat)}
              </span>
            </div>
            <Heading className="text-lg font-bold leading-snug text-foreground-950 md:text-xl">
              <span className="transition-colors group-hover:text-primary-700">{rumor.title}</span>
            </Heading>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">{rumor.excerpt}</p>
            <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-star-line" />
                {rumor.insider}
              </span>
              <span>{rumor.publishedAt}</span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-colors hover:border-primary-200 animate-fade-up"
      style={{ animationDelay: delay }}
    >
      <Link to={`/article/${rumor.id}`} className="block">
        <div className="h-44 w-full overflow-hidden">
          <img
            src={rumor.cover}
            alt={`${rumor.title} - 热点内幕独家爆料`}
            title={`${rumor.title} - 热点内幕`}
            className="h-full w-full object-top object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          {categoryBadge}
          <span className="flex shrink-0 items-center gap-1 text-xs text-foreground-500">
            <i className="ri-fire-line text-primary-500" />
            {formatHeat(rumor.heat)}
          </span>
        </div>
        <Heading className="text-base font-semibold leading-snug text-foreground-950">
          <Link to={`/article/${rumor.id}`} className="transition-colors hover:text-primary-700 line-clamp-2">
            {rumor.title}
          </Link>
        </Heading>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600 line-clamp-3">{rumor.excerpt}</p>
        <div className="mt-3 flex items-center justify-between border-t border-background-200/80 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1 truncate">
            <i className="ri-user-star-line" />
            {rumor.insider}
          </span>
          <span className="shrink-0">{shortDate(rumor.publishedAt)}</span>
        </div>
      </div>
    </article>
  );
}